import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, of, tap } from 'rxjs';

import { ApiResponse, FrontendDiscountType } from './api.models';
import { SAVING_ARENA_API_CONFIG } from './api.config';

/**
 * The live set of discount-page URL suffixes (`student-discount`, `military-discount`, …).
 *
 * Why this exists as module state rather than a normal injectable: `discountPageMatcher` in
 * app.routes.ts is a plain synchronous function the router calls during URL matching, so it cannot
 * inject anything or await a request. It reads this registry instead, which an app initializer
 * fills before the first navigation.
 *
 * The suffix list can't be compiled in, because admins add and rename discount types at runtime;
 * and the matcher can't just look for any `*-discount` URL, because ~1.3k store slugs already end
 * that way and would all be swallowed.
 */

// Seeded with the three original types so routing still works on the very first paint and if the
// API call fails — a stale list degrades to "this new type 404s until the next load", whereas an
// empty list would 404 every discount page.
const FALLBACK_SUFFIXES = ['student-discount', 'teacher-discount', 'military-discount'];

let suffixes: string[] = [...FALLBACK_SUFFIXES];

// Angular SSR bootstraps the app once per request, so the initializer below would otherwise fire a
// lookup on the critical path of every single page render. Module state outlives the request on the
// server, so a short TTL collapses that to roughly one refresh per minute per server process while
// still picking up a newly added type without a restart.
const RELOAD_AFTER_MS = 60_000;
let loadedAtMs = 0;

/** True when `slug` is `<something>-<a known discount suffix>` — a single store's discount page. */
export function matchesDiscountSuffix(slug: string): boolean {
  if (!slug) return false;

  return suffixes.some((suffix) => {
    if (!suffix || !slug.endsWith(`-${suffix}`)) return false;
    // Require a non-empty store part: a bare "student-discount" is the hub, matched below.
    return slug.length > suffix.length + 1;
  });
}

/** True when `slug` is exactly a known suffix — the hub listing every store of that type. */
export function isDiscountHubSuffix(slug: string): boolean {
  return Boolean(slug) && suffixes.includes(slug);
}

/** Replaces the registry. Exported for tests and the initializer below. */
export function setDiscountTypeSuffixes(next: readonly string[]): void {
  const cleaned = next.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);
  // An empty response is treated as "unknown", not "none" — see FALLBACK_SUFFIXES above.
  suffixes = cleaned.length > 0 ? cleaned : [...FALLBACK_SUFFIXES];
}

export function getDiscountTypeSuffixes(): readonly string[] {
  return suffixes;
}

/**
 * App initializer: loads the suffix list before the router's first navigation.
 *
 * Errors are swallowed — the fallback list keeps the existing pages routable, and failing to boot
 * the whole site because one lookup failed would be a much worse trade.
 */
export function loadDiscountTypeSuffixes() {
  const http = inject(HttpClient);
  const config = inject(SAVING_ARENA_API_CONFIG);

  const now = Date.now();
  if (loadedAtMs && now - loadedAtMs < RELOAD_AFTER_MS) return of(undefined);
  loadedAtMs = now;

  // apiBaseUrl already ends in /api/frontend — do not repeat it here.
  return http.get<ApiResponse<FrontendDiscountType[]>>(`${config.apiBaseUrl}/discount-types`).pipe(
    tap((response) => {
      if (response?.success && Array.isArray(response.data)) {
        setDiscountTypeSuffixes(response.data.map((type) => type.slugSuffix));
      }
    }),
    catchError(() => {
      // Let the next render retry rather than sitting on the fallback for a full TTL.
      loadedAtMs = 0;
      return of(null);
    }),
    map(() => undefined),
  );
}

/** Test seam: forget the TTL so the next call refetches. */
export function resetDiscountTypeCache(): void {
  loadedAtMs = 0;
  suffixes = [...FALLBACK_SUFFIXES];
}
